import SaveIcon from '@mui/icons-material/Save';
import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikIOSSwitch } from '~/components/Form/Switch/FormikIOSSwitch';
import { FormikTextField } from '~/components/Form/TextField';
import { CustomerFormFields } from '~/types/customer';

import { GenericDialog } from '../GenericDialog';

import { CustomerFormField, tabs } from './config';
import { validationSchema } from './validationSchema';

type Props = {
  title: string;
  open: boolean;
  initialValues: CustomerFormFields;
  onClose: () => void;
  onSubmit: (values: CustomerFormFields, formikHelpers: FormikHelpers<CustomerFormFields>) => void;
  isLoading: boolean;
};

export const CustomerFormDialog = ({
  title,
  open,
  initialValues,
  onClose,
  onSubmit,
  isLoading,
}: Props) => {
  const { t } = useTranslation();

  const [tabIndex, setTabIndex] = useState(0);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ submitForm, resetForm }) => (
        <GenericDialog
          title={t(title)}
          open={open}
          closeBtnLabel={t('msg_btn_cancel')}
          onClose={() => {
            resetForm();
            onClose();
          }}
          submitBtnLabel={t('msg_btn_save')}
          submitBtnIcon={<SaveIcon />}
          onSubmit={submitForm}
          isLoading={isLoading}
        >
          <ToggleButtonGroup
            color="primary"
            exclusive
            onChange={(_, value) => setTabIndex(value)}
            sx={{ mb: 3 }}
          >
            {tabs.map((tab, index) => (
              <ToggleButton key={tab} value={index} selected={tabIndex === index} color="standard">
                {t(tab)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {tabIndex == 0 && (
            <Stack gap={1}>
              <FormikTextField
                name={CustomerFormField.name}
                label={t('msg_label_name')}
                variant="filled"
                color="grey"
                autoFocus
                fullWidth
              />
              <FormikTextField
                name={CustomerFormField.company_number}
                label={t('msg_pdf_invoice_customer_company_number')}
                variant="filled"
                color="grey"
                fullWidth
              />
              <FormikTextField
                name={CustomerFormField.vat_number}
                label={t('msg_label_vat_code')}
                variant="filled"
                color="grey"
                fullWidth
              />
              <FormikTextField
                name={CustomerFormField.address}
                label={t('msg_label_company_post_address')}
                variant="filled"
                color="grey"
                fullWidth
              />
            </Stack>
          )}

          {tabIndex == 1 && (
            <Stack gap={1}>
              <FormikTextField
                name={CustomerFormField.email}
                label={t('msg_label_email')}
                variant="filled"
                color="grey"
                autoFocus
                fullWidth
              />
              <FormikTextField
                name={CustomerFormField.phone}
                label={t('msg_label_phone')}
                variant="filled"
                color="grey"
                fullWidth
              />
              <FormikTextField
                name={CustomerFormField.website}
                label={t('msg_label_website')}
                variant="filled"
                color="grey"
                fullWidth
              />
              <FormikTextField
                name={CustomerFormField.contact_person}
                label={t('msg_label_contact_person')}
                variant="filled"
                color="grey"
                fullWidth
              />
            </Stack>
          )}

          {tabIndex == 2 && (
            <FormikIOSSwitch
              name={CustomerFormField.soft_collection}
              label={t('msg_label_soft_collection_remind_customer')}
              sxStyle={{ gap: 1.5 }}
              labelPlacement="start"
            />
          )}
        </GenericDialog>
      )}
    </Formik>
  );
};
